import axios from 'axios';
import React, { createContext, useContext, useState } from 'react';
import DeezerService from '../services/deezerService';
import SpotifyService from '../services/spotifyService';

interface User {
  id: string;
  display_name?: string;
  firstName?: string;
  lastName?: string;
  email: string;
  images?: Array<{
    url: string;
    height: number;
    width: number;
  }>;
  role?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  accessToken: string | null;
  spotifyAccessToken: string | null;
  login: (platform?: 'web' | 'mobile', service?: 'spotify' | 'deezer') => void;
  loginWithCredentials: (
    email: string,
    password: string
  ) => Promise<{ success: boolean; error?: string }>;
  register: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  checkAuth: () => Promise<boolean>;
  requestPasswordReset: (email: string) => Promise<{ success: boolean; error?: string }>;
  resetPassword: (
    token: string,
    newPassword: string
  ) => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [spotifyAccessToken, setSpotifyAccessToken] = useState<string | null>(null);

  const checkAuth = async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      return false;
    }

    try {
      const API_URL = process.env.REACT_APP_FRONT_API_URL;
      const response = await axios.get(`${API_URL}/auth/customer/verify`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.valid && response.data.customer) {
        setIsAuthenticated(true);
        setUser(response.data.customer);
        setAccessToken(token);
        return true;
      }

      const spotifyResponse = await SpotifyService.checkToken(token);
      if (spotifyResponse.valid && spotifyResponse.user) {
        setIsAuthenticated(true);
        setUser(spotifyResponse.user);
        setAccessToken(token);
        if (spotifyResponse.refreshed && spotifyResponse.token && spotifyResponse.spotify) {
          localStorage.setItem('access_token', spotifyResponse.token);
          localStorage.setItem('spotify_access', spotifyResponse.spotify);
          setAccessToken(spotifyResponse.token);
          setSpotifyAccessToken(spotifyResponse.spotify);
        }
        return true;
      }

      // Si les deux échouent, déconnecter l'utilisateur
      logout();
      return false;
    } catch (error) {
      console.error("Erreur lors de la vérification de l'authentification:", error);
      logout();
      return false;
    }
  };

  const login = (platform: 'web' | 'mobile' = 'web', service: 'spotify' | 'deezer' = 'spotify') => {
    if (service === 'spotify') {
      SpotifyService.initiateLogin(platform);
    } else if (service === 'deezer') {
      DeezerService.initiateLogin(platform);
    }
  };

  const loginWithCredentials = async (
    email: string,
    password: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const API_URL = process.env.REACT_APP_FRONT_API_URL;
      const response = await axios.post(`${API_URL}/auth/customer/login`, { email, password });

      if (response.data.token && response.data.customer) {
        localStorage.setItem('access_token', response.data.token);
        localStorage.removeItem('spotify_access');
        setIsAuthenticated(true);
        setUser(response.data.customer);
        setAccessToken(response.data.token);
        setSpotifyAccessToken(null);
        return { success: true };
      } else {
        return { success: false, error: 'Réponse invalide du serveur' };
      }
    } catch (error: any) {
      console.error('Erreur lors de la connexion:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Erreur lors de la connexion',
      };
    }
  };

  const register = async (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }): Promise<{ success: boolean; error?: string }> => {
    try {
      const API_URL = process.env.REACT_APP_FRONT_API_URL;
      const response = await axios.post(`${API_URL}/auth/customer/register`, data);

      if (response.data.token && response.data.customer) {
        localStorage.setItem('access_token', response.data.token);
        localStorage.removeItem('spotify_access');
        setIsAuthenticated(true);
        setUser(response.data.customer);
        setAccessToken(response.data.token);
        setSpotifyAccessToken(null);
        return { success: true };
      } else {
        return { success: false, error: 'Réponse invalide du serveur' };
      }
    } catch (error: any) {
      console.error("Erreur lors de l'inscription:", error);
      return {
        success: false,
        error: error.response?.data?.message || "Erreur lors de l'inscription",
      };
    }
  };

  const requestPasswordReset = async (
    email: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const API_URL = process.env.REACT_APP_FRONT_API_URL;
      const response = await axios.post(`${API_URL}/auth/forgot-password`, { email });

      if (response.data.success) {
        return { success: true };
      } else {
        return {
          success: false,
          error: response.data.message || 'Erreur lors de la demande de réinitialisation',
        };
      }
    } catch (error: any) {
      console.error('Erreur lors de la demande de réinitialisation:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Erreur lors de la demande de réinitialisation',
      };
    }
  };

  const resetPassword = async (
    token: string,
    newPassword: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const API_URL = process.env.REACT_APP_FRONT_API_URL;
      const response = await axios.post(`${API_URL}/auth/reset-password`, { token, newPassword });

      if (response.data.success) {
        return { success: true };
      } else {
        return {
          success: false,
          error: response.data.message || 'Erreur lors de la réinitialisation du mot de passe',
        };
      }
    } catch (error: any) {
      console.error('Erreur lors de la réinitialisation du mot de passe:', error);
      return {
        success: false,
        error:
          error.response?.data?.message || 'Erreur lors de la réinitialisation du mot de passe',
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('spotify_access');
    setIsAuthenticated(false);
    setUser(null);
    setAccessToken(null);
    setSpotifyAccessToken(null);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        accessToken,
        spotifyAccessToken,
        login,
        loginWithCredentials,
        register,
        logout,
        checkAuth,
        requestPasswordReset,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth doit être utilisé à l'intérieur d'un AuthProvider");
  }
  return context;
};
